import { z } from 'zod';

/**
 * Flexible webhook payload schema
 * Uses passthrough to accept unknown fields and handle variations in Smartlead webhook structure
 */
export const smartleadWebhookSchema = z
  .object({
    // Core fields
    webhook_id: z.number().optional(),
    webhook_name: z.string().nullish(), // Allow null (Smartlead may send null)
    webhook_url: z.string().nullish(), // Allow null (Smartlead may send null)
    event_type: z.string(),
    event_timestamp: z.string().nullish(), // Allow null (Smartlead may send null)
    created_at: z.string().nullish(), // Allow null (Smartlead may send null)
    secret_key: z.string().nullish(), // Optional - Pabbly may not include it, Smartlead may send null

    // Lead identification (canonical and deprecated)
    lead_id: z.number(),
    to_email: z.string().nullish(), // Allow null (Smartlead may send null)
    to: z.string().nullish(), // deprecated - Allow null
    lead_email: z.string().nullish(), // deprecated - Allow null
    to_name: z.string().nullish(), // Allow null (Smartlead sends null when name is not available)
    lead_name: z.string().nullish(), // deprecated - Allow null (Smartlead sends null when name is not available)
    from_email: z.string().nullish(), // Allow null (Smartlead may send null)
    from: z.string().nullish(), // deprecated - Allow null

    // Campaign information
    campaign_id: z.number(),
    campaign_name: z.string().nullish(), // Allow null (Smartlead may send null)
    campaign_status: z.string().nullish(), // Allow null (Smartlead may send null)
    client_id: z.number().nullable().optional(),

    // Category information (canonical and deprecated)
    category: z.string().nullish(), // deprecated - Allow null (Smartlead may send null)
    lead_category_id: z.number().optional(), // deprecated
    lead_category: z
      .object({
        old_id: z.number().nullable().optional(),
        old_name: z.string().nullable().optional(),
        new_id: z.number().nullable().optional(), // Allow null (Smartlead may send null)
        new_name: z.string().nullish(), // Allow null (Smartlead may send null)
      })
      .optional(),

    // Lead data
    lead_data: z
      .object({
        email: z.string().nullish(),
        first_name: z.string().nullish(),
        last_name: z.string().nullish(),
        phone_number: z.string().nullish(),
        company_name: z.string().nullish(),
        website: z.string().nullish(),
        location: z.string().nullish(),
        linkedin_profile: z.string().nullish(),
        company_url: z.string().nullish(),
        custom_fields: z.record(z.any()).nullish(),
      })
      .optional(),

    // Reply information (canonical and deprecated)
    lastReply: z
      .object({
        type: z.string().nullish(), // Allow null (Smartlead may send null)
        time: z.string().nullish(), // Allow null (Smartlead may send null)
        email_body: z.string().nullish(), // Allow null (Smartlead may send null)
        message_id: z.string().nullish(), // Allow null (Smartlead may send null)
        stats_id: z.string().nullish(), // Allow null (Smartlead may send null)
      })
      .optional(), // deprecated
    last_reply: z
      .object({
        type: z.string().nullish(), // Allow null (Smartlead may send null)
        time: z.string().nullish(), // Allow null (Smartlead may send null)
        email_body: z.string().nullish(), // Allow null (Smartlead may send null)
        message_id: z.string().nullish(), // Allow null (Smartlead may send null)
        stats_id: z.string().nullish(), // Allow null (Smartlead may send null)
      })
      .optional(),

    // History array (not stored, but present in payload)
    history: z.array(z.any()).optional(),

    // Other fields
    app_url: z.string().nullish(), // Allow null (Smartlead may send null)
    ui_master_inbox_link: z.string().nullish(), // deprecated - Allow null (Smartlead may send null)
    description: z.string().nullish(), // Allow null (Smartlead may send null)
    metadata: z
      .object({
        webhook_created_at: z.string().nullish(), // Allow null (Smartlead may send null)
      })
      .optional(),
  })
  .passthrough(); // Allow unknown fields for flexibility

export type SmartleadWebhookPayload = z.infer<typeof smartleadWebhookSchema>;